import { authAPIClient } from '../lib/authAPI';

class MethodologyService {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Get methodology for a control ID
   * @param {string} controlId - The control ID (e.g., 'ML1-PA-01')
   * @param {string} userId - Optional user ID for custom methodologies
   * @returns {Object} Methodology object with text and metadata
   */
  async getMethodology(controlId, userId = null) {
    const cacheKey = `${userId || 'default'}_${controlId}`;
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      // Call the Edge Function
      const response = await authAPIClient.callFunction('get-methodology', {
        control_id: controlId,
        user_id: userId
      });

      if (response.success && response.data) {
        const methodologyData = {
          methodology: response.data.methodology,
          is_custom: response.data.is_custom,
          is_fallback: false
        };
        
        // Cache the result
        this.cache.set(cacheKey, methodologyData);
        return methodologyData;
      }
    } catch (error) {
      console.error('Failed to fetch methodology from Supabase API:', error);
    }

    // Return minimal fallback methodology if API fails
    console.warn(`Failed to load methodology for ${controlId}, using fallback`);
    const fallbackMethodology = {
      methodology: `Review and verify the implementation of control ${controlId}. Examine relevant documentation, configurations, and evidence to determine compliance with security requirements. Document findings and assess effectiveness of current implementation.`,
      is_custom: false,
      is_fallback: true,
      is_error: false
    };
    
    // Cache the fallback
    this.cache.set(cacheKey, fallbackMethodology);
    return fallbackMethodology;
  }

  /**
   * Save a user's custom methodology
   * @param {string} controlId - The control ID
   * @param {string} methodologyText - The custom methodology text
   * @returns {Object} Success/error result
   */
  async saveUserMethodology(controlId, methodologyText) {
    try {
      const response = await authAPIClient.callFunction('save-user-methodology', {
        control_id: controlId,
        methodology_text: methodologyText
      });

      if (response.success) {
        // Clear cache for this control for all users
        const keysToDelete = [];
        for (const key of this.cache.keys()) {
          if (key.endsWith(`_${controlId}`)) {
            keysToDelete.push(key);
          }
        }
        keysToDelete.forEach(key => this.cache.delete(key));

        return { success: true };
      }

      return { success: false, error: response.error || 'Failed to save methodology' };
    } catch (error) {
      console.error('Error saving user methodology:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Reset methodology to default (remove user customization)
   * @param {string} controlId - The control ID
   * @returns {Object} Success/error result
   */
  async resetToDefault(controlId) {
    try {
      const response = await authAPIClient.callFunction('reset-methodology', {
        control_id: controlId
      });

      if (response.success) {
        // Clear cache for this control for all users
        const keysToDelete = [];
        for (const key of this.cache.keys()) {
          if (key.endsWith(`_${controlId}`)) {
            keysToDelete.push(key);
          }
        }
        keysToDelete.forEach(key => this.cache.delete(key));

        return { success: true };
      }

      return { success: false, error: response.error || 'Failed to reset methodology' };
    } catch (error) {
      console.error('Error resetting methodology:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Clear all cached methodologies
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache stats for debugging
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Preload methodologies for better performance
   * @param {string[]} controlIds - Array of control IDs to preload
   * @param {string} userId - User ID for custom methodologies
   */
  async preloadMethodologies(controlIds, userId = null) {
    const promises = controlIds.map(controlId => 
      this.getMethodology(controlId, userId).catch(error => {
        console.warn(`Failed to preload methodology for ${controlId}:`, error);
        return null;
      })
    );

    try {
      await Promise.all(promises);
      console.log(`Preloaded ${controlIds.length} methodologies`);
    } catch (error) {
      console.error('Error preloading methodologies:', error);
    }
  }
}

// Export singleton instance
export const methodologyService = new MethodologyService();
export default methodologyService;